/**
 * "Also seen on" — every source URL a published event has been seen on
 * (owner ruling R30).
 *
 * **One caller left: the `refresh_review` HITL panel**, which has only
 * `problem.sources`. The event edit page used to render this too, as the
 * `EventSourcesCard` this module also exported; that card is gone, replaced by
 * "Refresh traces" (`components/common/refresh-traces.tsx`), which reads the
 * event's refresh history off Tina's own `GET /refresh-jobs` rather than off
 * the event's `metadata`. So `readEventSources` stays, for the HITL panel.
 *
 * Read-only on purpose. The list is written by Tina's refresh pipeline — the
 * duplicate hook adds an entry whenever another site's page is caught as a
 * duplicate of this event — and there is no route to edit it; a curator acts on
 * it by opening the source, not by correcting the record.
 *
 * **Renders nothing when there is nothing to show.** An event Tina has only ever
 * seen on one page has no second source, and an empty "Also seen on" list reads
 * as a broken one — the same rule `EntityTinaTrace` follows.
 *
 * **The list can be shorter than the event's, on purpose.** `RefreshHeader`
 * drops the entry for the page currently under review before it gets here
 * (`otherSightings`, owner ruling R76) — "also" is a claim about other pages,
 * and the page being decided on is not corroboration of itself. The filter lives
 * at that call site, not in here, for exactly that reason.
 */
import { Link } from "react-router";

import { ArrowUpRight, Network } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { readEventSources, sourceLabel } from "@/lib/event-sources";
import { cn } from "@/lib/utils";
import type { IEventSource } from "@/types/event";

/** The heading the section has always carried, kept so it reads the same on the panel it is now alone on. */
const HEADING = "Also seen on";

interface EventSourcesListProps {
  /**
   * The `sources` array, or the whole `metadata` block — `readEventSources`
   * takes either. Typed `unknown` deliberately: see that module's docstring for
   * why neither caller actually holds a checked value.
   */
  sources?: unknown;
  className?: string;
}

/** The bare heading-plus-rows form, for a surface that supplies its own chrome. */
export function EventSourcesList({
  sources,
  className,
}: EventSourcesListProps) {
  const entries = readEventSources(sources);
  if (entries.length === 0) return null;

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      <h3 className="text-xs uppercase tracking-wide text-muted-foreground">
        {HEADING}
      </h3>
      <ul className="flex flex-col gap-2">
        {entries.map((entry) => (
          <EventSourceRow key={entry.url} source={entry} />
        ))}
      </ul>
    </div>
  );
}

function EventSourceRow({ source }: { source: IEventSource }) {
  const label = sourceLabel(source);
  const fields = source.updatedFields ?? [];

  return (
    <li className="flex flex-col gap-1 rounded-md border bg-muted/20 p-2">
      <div className="flex flex-wrap items-center gap-2">
        <a
          href={source.url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex min-w-0 items-center gap-1 text-sm font-medium hover:underline"
        >
          <span className="truncate">{label}</span>
          <ArrowUpRight className="h-3 w-3 shrink-0 text-muted-foreground" />
        </a>
        {source.tinaItemId && (
          // Internal SPA route, so `Link`, not an `<a href>` — matches how
          // `EntityTinaTrace` navigates to the same `/tina/items/show/:id`
          // page. `Network`, not `ArrowUpRight`: that icon already means "open
          // the source URL" on the link above.
          <Link
            to={`/tina/items/show/${source.tinaItemId}`}
            className="inline-flex shrink-0 items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          >
            <Network className="h-3 w-3" />
            Tina item
          </Link>
        )}
      </div>
      {source.seenAt && (
        <span className="text-xs text-muted-foreground">
          last compared{" "}
          {/* A plain `title`, not `makeTooltip`: this row is read-only and
              renders inside whatever chrome its caller supplies, so a Radix
              tooltip would throw outside a `TooltipProvider` — a dependency it
              should not carry. The raw ISO value belongs on hover either way —
              `seenAt` is UTC and the label is local, and a curator diffing it
              against a Tina log needs the value Tina actually stored. */}
          <span className="font-mono tabular-nums" title={source.seenAt}>
            {formatSeenAt(source.seenAt)}
          </span>
        </span>
      )}
      {fields.length > 0 && (
        <div className="flex flex-wrap items-center gap-1">
          {/* What this URL actually changed on the event — the reason the entry
              is worth a curator's attention rather than just provenance. */}
          <span className="text-[11px] text-muted-foreground">updated</span>
          {fields.map((field) => (
            <Badge
              key={field}
              variant="secondary"
              className="text-[10px] font-normal"
            >
              {field}
            </Badge>
          ))}
        </div>
      )}
    </li>
  );
}

/**
 * `seenAt` in local time, or the raw string when it will not parse.
 *
 * Deliberately not `dayjs`-formatted-or-nothing: an unparseable timestamp is
 * still evidence, and "Invalid Date" is the failure the `movies`/`people`
 * column fix called out.
 */
function formatSeenAt(seenAt: string): string {
  const parsed = new Date(seenAt);
  if (Number.isNaN(parsed.getTime())) return seenAt;
  return parsed.toLocaleString(undefined, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}
